import React from 'react';
import { findIndex } from 'lodash';
import moment from 'moment-timezone';
import { useModalContext } from '@magoosh/context/modal_context';
import { useUserContext } from '@magoosh/context/user_context';
import { StudyPlanActionsInterface } from '../../types';

import HoursPerDayPanel from '../hours_per_day_panel';
import MixedPracticeForm from '@magoosh/gre/adaptive_prep_v2/organisms/mixed_practice_form';
import StatusMessage from '@magoosh/gre/adaptive_prep_v2/lib/status_message';
import StudyItemCard from '@magoosh/gre/adaptive_prep_v2/organisms/study_item_card';
import ConstructionIcon from '@magoosh/gre/adaptive_prep_v2/assets/icons/construction_icon.svg';
import {
  ChunkCompleted,
  EndOfPlanPreview,
  SeeMore,
  SeeMoreWithTopicLibraryNudge,
  SeeTomorrow
} from './actions_and_statuses';
import SecondaryCTA from '../../secondary_actions';
import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  adaptiveHoursPerChunk: number;
  chunks: any[];
  isLoadingMoreChunks: boolean;
  isLoading: boolean;
  prioritizedCheckpoint: React.ReactNode;
  setAdaptiveHoursPerChunk: (hours: number) => void;
  studyPlanIsPresent: boolean;
}

export const ToDoTab: React.FC<Props & StudyPlanActionsInterface> = (props) => {
  const timeZone =
    useUserContext().timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const {
    adaptiveHoursPerChunk,
    chunks,
    fetchMoreChunks,
    isLoadingMoreChunks,
    isLoading,
    prioritizedCheckpoint,
    setAdaptiveHoursPerChunk,
    studyPlanIsPresent
  } = props;

  const { setModal } = useModalContext();

  const handleMixedPracticeClicked = (id: number) => {
    setModal({
      header: 'Customize Your Session',
      body: <MixedPracticeForm studyPlanItemId={id} />
    });
  };

  const isFirstChunkSubjectToChange = (chunkIndex: number) => {
    return chunkIndex === findIndex(chunks, (c: any) => c.isSubjectToChange);
  };

  const renderChunkTitle = (chunkIndex: number) => {
    const chunk = chunks[chunkIndex];

    if (chunkIndex == 0) {
      const now = moment().tz(timeZone);

      return (
        <>
          <strong>Today:</strong> {now.format('dddd, MMMM D, YYYY')}
        </>
      );
    } else if (chunk.isSubjectToChange) {
      return <strong>Next Study Day (Preview)</strong>;
    } else {
      return <strong>Next Study Day</strong>;
    }
  };

  const nextAction = () => {
    if (!studyPlanIsPresent || !chunks) return null;

    if (chunks.length === 1) {
      return <SeeTomorrow isLoadingMoreChunks={isLoadingMoreChunks} fetchMoreChunks={fetchMoreChunks} />;
    } else if (!chunks[chunks.length - 1]?.isSubjectToChange) {
      return <SeeMore isLoadingMoreChunks={isLoadingMoreChunks} fetchMoreChunks={fetchMoreChunks} />;
    } else if (chunks.length < 10) {
      // TODO - update with per study plan limit on how many chunks to show
      return (
        <SeeMoreWithTopicLibraryNudge
          isLoadingMoreChunks={isLoadingMoreChunks}
          fetchMoreChunks={fetchMoreChunks}
        />
      );
    } else {
      return <EndOfPlanPreview />;
    }
  };

  return (
    <>
      {isLoading ? (
        <StatusMessage headline="How exciting!" text="Your study plan is almost ready..." isColored={true} />
      ) : (
        <>
          {prioritizedCheckpoint}

          <div>
            {chunks &&
              chunks.map((chunk, chunkIndex) => (
                <div key={chunkIndex}>
                  {isFirstChunkSubjectToChange(chunkIndex) && (
                    <div
                      className="d-flex border-radius u-margin-V-xl u-padding-A-s"
                      style={{ backgroundColor: colors.alertInfoBg, color: colors.grayDarker }}>
                      <div className="u-margin-R-s">
                        <img
                          style={{ minWidth: 30 }}
                          src={ConstructionIcon}
                          alt="Hammer and a pile of bricks"
                        />
                      </div>

                      <div>
                        The activities below are a preview based on your current performance and may change as
                        we learn more about your score improvement opportunity.
                      </div>
                    </div>
                  )}

                  {chunkIndex !== 0 && <hr />}

                  <div className="d-flex align-items-start align-items-sm-center flex-column flex-sm-row justify-content-between">
                    <div>
                      <h4 className="u-margin-V-s">
                        <strong>{renderChunkTitle(chunkIndex)}</strong>
                      </h4>
                    </div>
                    {chunkIndex === 0 && (
                      <div>
                        <HoursPerDayPanel
                          hours={adaptiveHoursPerChunk}
                          onSelectHoursOption={setAdaptiveHoursPerChunk}
                        />
                      </div>
                    )}
                  </div>

                  {chunkIndex === 0 && chunk.studyPlanItems.length === 0 ? (
                    <ChunkCompleted />
                  ) : (
                    chunk.studyPlanItems.map((item, itemIndex) => (
                      <StudyItemCard
                        key={`${chunkIndex}-${itemIndex}`}
                        type={item.type}
                        section={item.section}
                        variant={item.type === 'Segment' || item.type === 'Lesson' ? 'secondary' : 'info'}
                        headline={item.headline}
                        subheadline={item.subheadline}
                        significanceLevel={item.significanceLevel}
                        minutes={item.estimatedTimeMinutes}
                        id={item.id}
                        primaryCTA={
                          chunk.isSubjectToChange
                            ? null
                            : {
                                ...item.primaryCta,
                                variant:
                                  chunkIndex === 0 && itemIndex === 0 && !prioritizedCheckpoint
                                    ? 'secondary'
                                    : 'default',
                                onClick:
                                  item.type === 'Practice'
                                    ? () => handleMixedPracticeClicked(item.id)
                                    : undefined
                              }
                        }
                        secondaryCTA={<SecondaryCTA chunk={chunk} item={item} planType="adaptive" />}
                        isLarger={chunkIndex === 0 && itemIndex === 0 && !prioritizedCheckpoint}
                        description={item.description}
                        shortDescription={item.shortDescription}
                        locked={item.locked}
                        isStrategy={item.isStrategy}
                        isCompleted={!!item.completedAt}
                      />
                    ))
                  )}
                </div>
              ))}
          </div>

          {nextAction()}
        </>
      )}
    </>
  );
};

export default ToDoTab;
